import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  adminGetProduct,
  adminSaveProduct,
  adminDeleteProduct,
  adminAddProductFile,
  adminDeleteProductFile,
  adminSignProductFileUpload,
} from "@/lib/store-admin.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export const Route = createFileRoute("/admin/store/products/$id")({
  component: ProductEditor,
});

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function bytesToHuman(b?: number | null) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function ProductEditor() {
  const { id } = Route.useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const getProduct = useServerFn(adminGetProduct);
  const saveProduct = useServerFn(adminSaveProduct);
  const deleteProduct = useServerFn(adminDeleteProduct);
  const addFile = useServerFn(adminAddProductFile);
  const deleteFile = useServerFn(adminDeleteProductFile);
  const signUpload = useServerFn(adminSignProductFileUpload);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "product", id],
    queryFn: () => getProduct({ data: { id } }),
    enabled: !isNew,
  });

  const [form, setForm] = useState({
    id: undefined as string | undefined,
    slug: "",
    title: "",
    subtitle: "",
    description: "",
    price_cents: 0,
    product_type: "teaching",
    category: "stewardship",
    thumbnail_url: "" as string,
    preview_url: "" as string,
    pages: "" as string,
    duration_minutes: "" as string,
    featured: false,
    published: false,
  });
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (data?.product) {
      const p = data.product;
      setForm({
        id: p.id,
        slug: p.slug,
        title: p.title,
        subtitle: p.subtitle ?? "",
        description: p.description ?? "",
        price_cents: p.price_cents,
        product_type: p.product_type,
        category: p.category,
        thumbnail_url: p.thumbnail_url ?? "",
        preview_url: p.preview_url ?? "",
        pages: p.pages ? String(p.pages) : "",
        duration_minutes: p.duration_minutes ? String(p.duration_minutes) : "",
        featured: p.featured,
        published: p.published,
      });
      setSlugTouched(true);
    }
  }, [data]);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function uploadToBucket(
    bucket: "product-thumbnails" | "product-previews" | "product-files",
    file: File,
    productId?: string,
  ) {
    const path = `${productId ?? "tmp"}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    const { token, publicUrl } = await signUpload({ data: { bucket, path } });
    const { error } = await supabase.storage.from(bucket).uploadToSignedUrl(path, token, file);
    if (error) throw new Error(error.message);
    return { path, publicUrl };
  }

  async function onThumbnailChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { publicUrl } = await uploadToBucket("product-thumbnails", file, form.id);
      set("thumbnail_url", publicUrl ?? "");
      toast.success("Thumbnail uploaded.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function onPreviewChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { publicUrl } = await uploadToBucket("product-previews", file, form.id);
      set("preview_url", publicUrl ?? "");
      toast.success("Preview uploaded.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function onProductFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || !form.id) {
      toast.error("Save the product first before uploading files.");
      return;
    }
    for (const file of Array.from(files)) {
      try {
        const { path } = await uploadToBucket("product-files", file, form.id);
        await addFile({
          data: {
            product_id: form.id,
            file_name: file.name,
            storage_path: path,
            mime_type: file.type || null,
            file_size_bytes: file.size,
          },
        });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Upload failed");
      }
    }
    toast.success("Files uploaded.");
    qc.invalidateQueries({ queryKey: ["admin", "product", id] });
  }

  async function onDeleteFile(fileId: string) {
    if (!confirm("Delete this file?")) return;
    try {
      await deleteFile({ data: { id: fileId } });
      qc.invalidateQueries({ queryKey: ["admin", "product", id] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  }

  async function onSave(publish: boolean) {
    try {
      const payload = {
        id: form.id,
        slug: form.slug || slugify(form.title),
        title: form.title,
        subtitle: form.subtitle || null,
        description: form.description || null,
        price_cents: Math.round(Number(form.price_cents) || 0),
        product_type: form.product_type,
        category: form.category,
        thumbnail_url: form.thumbnail_url || null,
        preview_url: form.preview_url || null,
        pages: form.pages ? Number(form.pages) : null,
        duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
        featured: form.featured,
        published: publish ? true : form.published,
      };
      const row = await saveProduct({ data: payload });
      toast.success(publish ? "Saved & published." : "Draft saved.");
      qc.invalidateQueries({ queryKey: ["admin", "store", "products"] });
      if (isNew && row?.id) {
        navigate({ to: "/admin/store/products/$id", params: { id: row.id } });
      } else {
        qc.invalidateQueries({ queryKey: ["admin", "product", id] });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed");
    }
  }

  async function onDelete() {
    if (!form.id || !confirm("Delete this product permanently?")) return;
    try {
      await deleteProduct({ data: { id: form.id } });
      toast.success("Deleted.");
      navigate({ to: "/admin/store" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  }

  if (!isNew && isLoading) {
    return (
      <PortalShell nav={ADMIN_NAV} title="Currency Thoughts Store">
        <p className="font-sans text-charcoal-500">Loading…</p>
      </PortalShell>
    );
  }

  const files = data?.files ?? [];

  return (
    <PortalShell nav={ADMIN_NAV} title="Currency Thoughts Store">
      <button
        onClick={() => navigate({ to: "/admin/store" })}
        className="font-sans text-xs uppercase tracking-[0.22em] text-bronze-600"
      >
        ← Back to library
      </button>
      <Eyebrow>{isNew ? "New resource" : "Edit resource"}</Eyebrow>
      <h1 className="mt-2 font-serif text-3xl text-navy-900">
        {form.title || "Untitled resource"}
      </h1>

      <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <Field label="Title">
            <Input
              value={form.title}
              onChange={(e) => {
                set("title", e.target.value);
                if (!slugTouched) set("slug", slugify(e.target.value));
              }}
            />
          </Field>
          <Field label="Slug">
            <Input
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                set("slug", slugify(e.target.value));
              }}
            />
          </Field>
          <Field label="Subtitle">
            <Input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
          </Field>
          <Field label="Description (markdown)">
            <Textarea
              rows={12}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              className="font-mono text-sm"
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Type">
              <select
                value={form.product_type}
                onChange={(e) => set("product_type", e.target.value)}
                className="w-full rounded-sm border border-bronze-600/30 bg-white px-3 py-2 font-sans text-sm"
              >
                {["teaching", "workbook", "course", "study-guide", "audio", "video", "bundle"].map(
                  (t) => (
                    <option key={t}>{t}</option>
                  ),
                )}
              </select>
            </Field>
            <Field label="Category">
              <select
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="w-full rounded-sm border border-bronze-600/30 bg-white px-3 py-2 font-sans text-sm"
              >
                {["stewardship", "household", "estate", "doctrine", "family-ed", "practical", "other"].map(
                  (t) => (
                    <option key={t}>{t}</option>
                  ),
                )}
              </select>
            </Field>
            <Field label="Price (USD)">
              <Input
                type="number"
                step="0.01"
                value={(form.price_cents / 100).toString()}
                onChange={(e) => set("price_cents", Math.round(Number(e.target.value) * 100))}
              />
            </Field>
            <Field label="Pages">
              <Input
                type="number"
                value={form.pages}
                onChange={(e) => set("pages", e.target.value)}
              />
            </Field>
            <Field label="Duration (minutes)">
              <Input
                type="number"
                value={form.duration_minutes}
                onChange={(e) => set("duration_minutes", e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Thumbnail">
              {form.thumbnail_url && (
                <img src={form.thumbnail_url} alt="" className="mb-2 h-32 w-32 rounded-sm object-cover" />
              )}
              <input type="file" accept="image/*" onChange={onThumbnailChange} />
            </Field>
            <Field label="Preview (optional)">
              {form.preview_url && (
                <a
                  href={form.preview_url}
                  target="_blank"
                  className="mb-2 block text-xs text-bronze-600 underline"
                  rel="noreferrer"
                >
                  View current preview
                </a>
              )}
              <input type="file" onChange={onPreviewChange} />
            </Field>
          </div>


          {/* Files */}
          <div className="rounded-sm border border-bronze-600/30 bg-white p-6">
            <Eyebrow>Files</Eyebrow>
            {!form.id && (
              <p className="mt-3 font-sans text-sm text-charcoal-500">
                Save a draft first to enable file uploads.
              </p>
            )}
            {form.id && (
              <>
                <input type="file" multiple onChange={onProductFiles} className="mt-3" />
                <ul className="mt-4 divide-y divide-bronze-600/20">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between py-3 font-sans text-sm">
                      <div>
                        <p className="text-navy-900">{f.file_name}</p>
                        <p className="text-xs text-charcoal-500">
                          {f.mime_type ?? "—"} · {bytesToHuman(f.file_size_bytes)}
                        </p>
                      </div>
                      <button onClick={() => onDeleteFile(f.id)} className="text-[#B43A3A]">
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-sm border border-bronze-600/30 bg-white p-6">
            <Eyebrow>Visibility</Eyebrow>
            <div className="mt-4 flex items-center justify-between font-sans text-sm">
              <span>Featured</span>
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} />
            </div>
            <div className="mt-3 flex items-center justify-between font-sans text-sm">
              <span>Published</span>
              <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} />
            </div>
          </div>
          <PrimaryButton onClick={() => onSave(false)} className="w-full">
            Save draft
          </PrimaryButton>
          <SecondaryButton onClick={() => onSave(true)} className="w-full">
            Save &amp; publish
          </SecondaryButton>
          {!isNew && (
            <button
              onClick={onDelete}
              className="w-full pt-4 font-sans text-xs uppercase tracking-[0.22em] text-[#B43A3A]"
            >
              Delete product
            </button>
          )}
        </aside>
      </div>
    </PortalShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-charcoal-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}